import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'
import { selectIsDrawerDismissed, selectIsDrawerMinimized } from '@/uppy-file-upload/store/selectors'
import { selectSideNavCollapsed } from '@/store/selectors/ui-state.selectors'

type UseUploadDrawerParams = {
  /** The total number of files currently being processed. */
  processingTotalCount: number
  /** Whether there are any active uploads. */
  hasActiveUploads: boolean
}

type UseUploadDrawerReturn = {
  /** Whether the drawer is visible. */
  visible: boolean
  /** Whether the drawer is expanded. */
  expanded: boolean
  /**
   * Left offset the drawer needs to clear the desktop side nav: `true` for the expanded nav,
   * `'rail'` for the contracted 48px rail, `false` when no nav is showing.
   */
  offsetForSidebar: boolean | 'rail'
  /** Called when the drawer expanded state should change. */
  onExpandedChange: (expanded: boolean) => void
  /** Called when the drawer close button is clicked. */
  onClose: () => void
}

export function useUploadDrawer({ processingTotalCount, hasActiveUploads }: UseUploadDrawerParams): UseUploadDrawerReturn {
  const dispatch = useAppDispatch()

  // Persisted in Redux so state survives DriveView remounts during folder navigation.
  const userDismissed = useAppSelector(selectIsDrawerDismissed)
  const userMinimized = useAppSelector(selectIsDrawerMinimized)
  const sideNavCollapsed = useAppSelector(selectSideNavCollapsed)

  // A collapsed nav still leaves the 48px rail, which the drawer would otherwise slide under.
  const offsetForSidebar = sideNavCollapsed ? 'rail' : true

  // null on first render so existing uploads on remount don't trigger drawerNewFilesAdded.
  const prevProcessingTotalCountRef = useRef<number | null>(null)

  useEffect(() => {
    // If the number of processing files has increased, trigger the drawerNewFilesAdded action.
    const prev = prevProcessingTotalCountRef.current
    if (prev !== null && processingTotalCount > prev) {
      dispatch(UppyFileUploadActions.drawerNewFilesAdded())
    }
    prevProcessingTotalCountRef.current = processingTotalCount
  }, [processingTotalCount, dispatch])

  return {
    visible: !userDismissed && hasActiveUploads,
    expanded: !userMinimized,
    offsetForSidebar,
    onExpandedChange: (expanded: boolean) => dispatch(UppyFileUploadActions.drawerExpandedChanged(expanded)),
    onClose: () => dispatch(UppyFileUploadActions.drawerDismissed()),
  }
}
